'use client';

// Mounts a single UpgradeModal at the app root and exposes open/close through
// context, so any client component (the PlanBadge in the workspace header, the
// in-editor UpgradePrompt, a future pricing CTA) can trigger the same dialog
// without each owning its own copy. The modal renders nothing until opened.
//
// Both dialogs are code-split (S6). This provider sits in the root layout, so
// without that every public page (a guide, a calculator, a room-size page)
// would download and parse the whole plans screen and the pre-upsell's live
// room for a dialog most of those visitors never open. React.lazy runs its
// factory on first RENDER, and neither dialog renders until open(): the chunk
// is fetched on the first open and the dialog paints once it lands, with
// nothing shown in between.

import { createContext, lazy, Suspense, useCallback, useContext, useMemo, useState } from 'react';
import { useNativeHost } from '@/components/room/state/NativeHostContext';
import type { UpgradeDismissReason, UpgradeModalSecondaryAction } from './UpgradeModal';
import type { PreUpsellShell } from '@/components/upsell/PreUpsell';
import type { DesignMethodId, OnboardingGoalId } from '@roomtreatment/room-flow';
import { EVENTS, isFeatureGate, track, type Trigger } from '@/lib/analytics';

const UpgradeModal = lazy(() => import('./UpgradeModal'));
const PreUpsellDialog = lazy(() => import('@/components/upsell/PreUpsellDialog'));

/** What the pre-upsell needs to personalise itself. Passing it turns the
 *  upgrade into a two-screen sequence: the treated room first, plans on
 *  Continue. Omit it and the modal opens straight onto plans, exactly as
 *  before — every existing call site is unchanged. */
export interface UpgradePrelude {
  goal: OnboardingGoalId | null;
  designMethods: DesignMethodId[];
  /** Which layout the pre-upsell uses. Omit for the shipped one — the
   *  exploration at /design/a|b|c sets it so those pages can drive this exact
   *  sequence, Continue included, instead of a stub that goes nowhere. */
  shell?: PreUpsellShell;
}

interface OpenOptions {
  /** The room a purchase should "spend in context" on — flows to checkout
   *  metadata so the webhook unlocks that exact room with one of the credits. */
  sceneToken?: string | null;
  /** Which upsell surface opened the modal — the spine of the per-upsell
   *  conversion analysis (flows through checkout to the server revenue event). */
  trigger?: Trigger;
  // There is deliberately no "not dismissible" option. The plans screen can
  // always be closed (X, Escape, backdrop), including when a limit was hit: a
  // pricing screen with no way out reads as a hard paywall. The only screen
  // without a close is the pre-upsell (`prelude`), whose Continue leads here.
  /** A plain-text "Continue free" style link shown next to the primary CTA.
   *  Calling it runs onClick then closes the modal. */
  secondaryAction?: UpgradeModalSecondaryAction;
  /** Show the personalised pre-upsell before the plans. See UpgradePrelude. */
  prelude?: UpgradePrelude;
}

interface UpgradeCloseDetail {
  reason?: UpgradeDismissReason;
  /** The plan highlighted when they left, if any. A dismissal WITH a plan
   *  selected is a very different signal from one without: they got as far as
   *  choosing and still did not buy. */
  packageId?: string | null;
}

interface UpgradeModalApi {
  open: (opts?: OpenOptions) => void;
  close: (detail?: UpgradeCloseDetail) => void;
  isOpen: boolean;
}

const UpgradeModalContext = createContext<UpgradeModalApi | null>(null);

export function UpgradeModalProvider({ children }: { children: React.ReactNode }) {
  const [isOpen, setIsOpen] = useState(false);
  const [sceneToken, setSceneToken] = useState<string | null>(null);
  const [trigger, setTrigger] = useState<Trigger | undefined>(undefined);
  const [secondaryAction, setSecondaryAction] = useState<UpgradeModalSecondaryAction | undefined>(undefined);
  const [prelude, setPrelude] = useState<UpgradePrelude | null>(null);
  const { isNative, capabilities } = useNativeHost();
  const open = useCallback((opts?: OpenOptions) => {
    // Inside the app a plan is only sold where the App Store allows an
    // external purchase link (the US storefront, ADR 009); the shell says so
    // through `capabilities.checkout`. Everywhere else, every path into this
    // modal (a header button, a room-limit 402, an exhausted credit pool, the
    // post-login reopen below) is a quiet no-op: one gate here rather than at
    // each of the dozen call sites.
    if (isNative && capabilities.checkout !== 'web') return;
    setSceneToken(opts?.sceneToken ?? null);
    setTrigger(opts?.trigger);
    setSecondaryAction(opts?.secondaryAction);
    setPrelude(opts?.prelude ?? null);
    setIsOpen(true);
    if (isFeatureGate(opts?.trigger)) {
      track(EVENTS.feature_gate_viewed, {
        trigger: opts?.trigger,
        scene_token: opts?.sceneToken ?? null,
      });
    }
    track('upgrade_modal_opened', { trigger: opts?.trigger, scene_token: opts?.sceneToken ?? null });
  }, [isNative, capabilities.checkout]);
  const close = useCallback((detail?: UpgradeCloseDetail) => {
    // Guarded on isOpen via the functional update: close() is safe to call
    // twice (Escape during an unmount, a secondary action that also closes),
    // and a second call must not emit a second dismissal.
    setIsOpen((wasOpen) => {
      if (wasOpen) {
        track(EVENTS.upgrade_modal_dismissed, {
          trigger,
          scene_token: sceneToken,
          reason: detail?.reason ?? 'programmatic',
          // Which of the two screens they were on. A prelude dismissal is
          // nearly impossible by design (its only exit is Continue), so a
          // non-zero count here means something else closed the modal.
          at_stage: prelude ? 'prelude' : 'plans',
          package_selected: detail?.packageId ?? null,
        });
      }
      return false;
    });
  }, [trigger, sceneToken, prelude]);
  const api = useMemo<UpgradeModalApi>(() => ({ open, close, isOpen }), [open, close, isOpen]);

  return (
    <UpgradeModalContext.Provider value={api}>
      {children}
      {/* Two screens, one sequence. `prelude` is cleared by Continue rather
          than tracked as a separate stage flag: "is there still a prelude to
          show" and "which screen am I on" are the same question, and one
          piece of state cannot disagree with itself.

          Mounted only while open (UpgradeModal rendered null when closed
          anyway): not rendering is what keeps the lazy chunks unfetched, and
          unmounting on close gives each open a fresh modal, so transient
          submit state (submitting / error) resets without a
          setState-in-effect. */}
      {isOpen && (
        <Suspense fallback={null}>
          {prelude ? (
            <PreUpsellDialog
              goal={prelude.goal}
              designMethods={prelude.designMethods}
              shell={prelude.shell}
              // Closable since 2026-09-19 (see PreUpsellDialog's header). The
              // dismissal lands in the same close() as the plans screen, so it
              // is tagged `at_stage: 'prelude'` below and is finally visible.
              onClose={(reason) => close({ reason })}
              onContinue={() => {
                track('upgrade_prelude_continued', { trigger, scene_token: sceneToken });
                setPrelude(null);
              }}
            />
          ) : (
            <UpgradeModal
              open
              onClose={close}
              sceneToken={sceneToken}
              trigger={trigger}
              secondaryAction={secondaryAction}
            />
          )}
        </Suspense>
      )}
    </UpgradeModalContext.Provider>
  );
}

export function useUpgradeModal(): UpgradeModalApi {
  const ctx = useContext(UpgradeModalContext);
  if (!ctx) throw new Error('useUpgradeModal must be used within an UpgradeModalProvider');
  return ctx;
}
